import React from 'react';
import { useController, type Control } from 'react-hook-form';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Input from '@amzn/meridian/input';
import TimePicker from '@amzn/meridian/time-picker';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import { useTranslation } from 'src/arb-wrapper';

interface TemplateScheduleWindowInputProps {
  control: Control;
  /** Full RHF path to the schedule window object (e.g. `${name}.scheduleWindows.LIVE_NOW`). */
  scheduleWindowPath: string;
  /** IANA timezone from the selected publisher account config; labels the FIXED time input. */
  publisherTimezone: string;
}

export const TemplateScheduleWindowInput: React.FC<TemplateScheduleWindowInputProps> = ({
  control,
  scheduleWindowPath,
  publisherTimezone,
}) => {
  const { t } = useTranslation();

  return (
    <Column spacing="300" spacingInset="300 none">
      <ScheduleFieldInput control={control} fieldPath={`${scheduleWindowPath}.goesLive`} label={t('schedule_window_goes_live')} publisherTimezone={publisherTimezone} />
      <ScheduleFieldInput control={control} fieldPath={`${scheduleWindowPath}.takenDown`} label={t('schedule_window_taken_down')} publisherTimezone={publisherTimezone} />
    </Column>
  );
};

interface ScheduleFieldInputProps {
  control: Control;
  fieldPath: string;
  label: string;
  publisherTimezone: string;
}

const ScheduleFieldInput: React.FC<ScheduleFieldInputProps> = ({ control, fieldPath, label, publisherTimezone }) => {
  const { t } = useTranslation();
  // NOTE: these fields intentionally do NOT use `shouldUnregister`. With it, RHF purges the
  // registered values whenever the inputs unmount (mode toggle, a live status being re-checked,
  // or a step/remount), which silently drops the user's schedule before submit. Leaving them
  // registered keeps the values in form state; stale cross-mode fields (e.g. an `offset` left on a
  // FIXED window) are harmless — the backend ValidationPipe runs with `whitelist: true` and strips them.
  const modeField = useController({ control, name: `${fieldPath}.mode`, defaultValue: 'FIXED' });
  const isEventMode = modeField.field.value === 'EVENT';

  return (
    <Column spacing="200">
      <Text color="secondary" type="b200">{label}</Text>
      <Row alignmentVertical="center" spacing="200">
        <Toggle
          checked={isEventMode}
          data-testid={`${fieldPath}-mode-toggle`}
          size="small"
          onChange={(checked: boolean) => { modeField.field.onChange(checked ? 'EVENT' : 'FIXED'); }}
        />
        <Text color="secondary" type="b200">
          {isEventMode ? t('schedule_window_mode_event') : t('schedule_window_mode_fixed')}
        </Text>
      </Row>
      {!isEventMode && <FixedFieldInputs control={control} fieldPath={fieldPath} publisherTimezone={publisherTimezone} />}
      {isEventMode && <EventFieldInputs control={control} fieldPath={fieldPath} />}
    </Column>
  );
};

const FixedFieldInputs: React.FC<{ control: Control; fieldPath: string; publisherTimezone: string }> = ({ control, fieldPath, publisherTimezone }) => {
  const { t } = useTranslation();
  const daysField = useController({ control, name: `${fieldPath}.daysBeforeEvent`, defaultValue: 0 });
  const timeField = useController({ control, name: `${fieldPath}.timeOfDay`, defaultValue: '00:00:00' });

  return (
    <Row alignmentVertical="center" spacing="200" wrap="down">
      <Input
        data-testid={`${fieldPath}-days`}
        size="small"
        type="number"
        value={String(daysField.field.value)}
        width={60}
        onChange={(value: string) => { daysField.field.onChange(Number(value)); }}
      />
      <Text color="secondary" type="b200">{t('schedule_window_days_before_event')}</Text>
      <Text color="secondary" type="b200">{t('schedule_window_at')}</Text>
      <TimePicker
        data-testid={`${fieldPath}-time`}
        value={timeField.field.value}
        onChange={(value: string) => { timeField.field.onChange(value); }}
      />
      <Text color="secondary" type="b200">
        {t('schedule_window_publisher_timezone', { timezone: publisherTimezone })}
      </Text>
    </Row>
  );
};

const EventFieldInputs: React.FC<{ control: Control; fieldPath: string }> = ({ control, fieldPath }) => {
  const { t } = useTranslation();
  const offsetField = useController({ control, name: `${fieldPath}.offset`, defaultValue: 0 });
  const unitField = useController({ control, name: `${fieldPath}.unit`, defaultValue: 'HOURS' });
  const directionField = useController({ control, name: `${fieldPath}.direction`, defaultValue: 'BEFORE' });
  const anchorField = useController({ control, name: `${fieldPath}.anchor`, defaultValue: 'EVENT_START' });

  return (
    <Row alignmentVertical="center" spacing="200" wrap="down">
      <Input
        data-testid={`${fieldPath}-offset`}
        size="small"
        type="number"
        value={String(offsetField.field.value)}
        width={60}
        onChange={(value: string) => { offsetField.field.onChange(Number(value)); }}
      />
      <Select
        data-testid={`${fieldPath}-unit`}
        size="small"
        value={unitField.field.value}
        width={100}
        onChange={(value: string) => { unitField.field.onChange(value); }}
      >
        <SelectOption label={t('schedule_window_unit_hours')} value="HOURS" />
        <SelectOption label={t('schedule_window_unit_days')} value="DAYS" />
      </Select>
      <Select
        data-testid={`${fieldPath}-direction`}
        size="small"
        value={directionField.field.value}
        width={100}
        onChange={(value: string) => { directionField.field.onChange(value); }}
      >
        <SelectOption label={t('schedule_window_direction_before')} value="BEFORE" />
        <SelectOption label={t('schedule_window_direction_after')} value="AFTER" />
      </Select>
      <Select
        data-testid={`${fieldPath}-anchor`}
        size="small"
        value={anchorField.field.value}
        width={140}
        onChange={(value: string) => { anchorField.field.onChange(value); }}
      >
        <SelectOption label={t('schedule_window_anchor_event_start')} value="EVENT_START" />
        <SelectOption label={t('schedule_window_anchor_event_end')} value="EVENT_END" />
      </Select>
    </Row>
  );
};
